import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import {
  BasicsNativeModule,
  ClientAuthNativeModule,
  ContentNativeModule,
  ContentPackNativeModule,
  MediaNativeModule,
  NetworkNativeModule,
  PermissionsNativeModule,
  StorageNativeModule,
} from '@hg-com/xwc-js-bridge/dist/modules';
import {
  BridgeMode,
  nativeAppBridge,
  NativeAppService,
} from '@hg-com/xwc-js-bridge/dist/native-app.service';
import { BehaviorSubject } from 'rxjs';
import { IBridgeAckOrError } from '@hg-com/xwc-js-bridge/dist/i-bridge-response';

/**
 * Mit den NativeIntegrationService kann man auf native Funktionalitäten eines Geräts zugreifen.
 *
 *   Die Methode "checkConnection" wird verwendet, um die Verbindung des Geräts zum Internet zu überprüfen.
 *
 *   Die Methode "readyForOfflineMode" wird verwendet, um festzustellen, ob das Gerät bereit ist, offline zu arbeiten. Es wird überprüft, ob bestimmte Daten- und Medienpakete bereits geladen wurden.
 *
 *   Die Methoden "vibrate", "vibrateClick", "hapticFeedbackClick" und "hapticFeedback" werden verwendet, um eine Rückmeldung auf Benutzeraktionen zu geben, indem sie das Gerät vibrieren oder haptisches Feedback geben.
 *
 */

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

@Injectable({
  providedIn: 'root',
})
export class NativeIntegrationService {
  online$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  offlineMode$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly isNative$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  public readonly basics: BasicsNativeModule;
  public readonly clientAuth: ClientAuthNativeModule;
  public readonly content: ContentNativeModule;
  public readonly contentPack: ContentPackNativeModule;
  public readonly media: MediaNativeModule;
  public readonly network: NetworkNativeModule;
  public readonly permissions: PermissionsNativeModule;
  public readonly native: NativeAppService;
  public readonly storage: StorageNativeModule;
  public readonly isReady$ = new BehaviorSubject<boolean>(false);

  constructor(private http: HttpClient, private zone: NgZone) {
    this.native = nativeAppBridge(zone, { production: true });
    this.basics = new BasicsNativeModule(this.native);
    this.clientAuth = new ClientAuthNativeModule(this.native);
    this.content = new ContentNativeModule(this.native);
    this.contentPack = new ContentPackNativeModule(this.native);
    this.media = new MediaNativeModule(this.native);
    this.network = new NetworkNativeModule(this.native);
    this.permissions = new PermissionsNativeModule(this.native);
    this.storage = new StorageNativeModule(this.native);

    this.spinTillReady().then(() => {
      if (this.native.mode > BridgeMode.Mock) {
        this.isNative$.next(true);
      }
    });
  }

  async spinTillReady() {
    if (this.isReady$.getValue()) {
      return;
    }
    let waitTime = 0;
    while (!(this.native.mode >= BridgeMode.Disabled)) {
      await sleep(50);
      waitTime += 50;
      if (waitTime > 2500) {
        this.native.msgToWebInit();
      }
    }
    if (this.native.mode > BridgeMode.Mock) {
      this.isNative$.next(true);
    }

    this.isReady$.next(true);
  }

  checkConnection(): Promise<void> {
    console.log('[NIS]: CONNECTION CHECK');
    return this.network.getInternetConnection$().then((connectionResult) => {
      if (
        connectionResult.data === 'mobile' ||
        connectionResult.data === 'lan'
      ) {
        this.online$.next(true);
      } else {
        this.online$.next(false);
      }
    });
  }

  async readyForOfflineMode(): Promise<boolean> {
    return await this.contentPack.list$().then((list) => {
      const dataList: any = Object.values(
        list.data.filter((ob: { packId: string }) => ob.packId === 'data')
      );
      const mediaList: any = Object.values(
        list.data.filter((ob: { packId: string }) => ob.packId === 'media')
      );
      return dataList[0].__loaded !== null && mediaList[0].__loaded !== null;
    });
  }

  vibrate(): void {
    // vibrates on smartphone
    if (this.native.mode > BridgeMode.Mock) {
      this.basics.vibrateLong$().then();
    }
  }

  vibrateClick(): void {
    if (this.native.mode > BridgeMode.Mock) {
      this.basics.vibrateClick$().then();
    }
  }

  hapticFeedbackClick(): void {
    if (this.native.mode > BridgeMode.Mock) {
      this.basics.hapticFeedbackClick$().then();
    }
  }

  hapticFeedback(): void {
    if (this.native.mode > BridgeMode.Mock) {
      this.basics.hapticFeedbackLong$().then();
    }
  }
}
